import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AiFillClockCircle, AiOutlineArrowRight } from "react-icons/ai";
import { BiSolidMessageAlt } from "react-icons/bi";

interface TechPost {
  id: number;
  name: string;
  avatar: string;
  description: string;
  tag: string;
}

/* Rich sample posts so the Technology section always renders, even
   without the backend running. */
const dummyTests: TechPost[] = [
  {
    id: 1,
    name: "The rise of quantum computing and what it means for you",
    avatar: "/blog/technology.png",
    description:
      "Quantum machines promise to solve problems that would take classical computers centuries. Here's how this breakthrough will reshape industries.",
    tag: "AI & ML",
  },
  {
    id: 2,
    name: "Top 5 programming languages to learn this year",
    avatar: "/blog/latest-1.png",
    description:
      "From Python's data dominance to Rust's safety-first design, discover which languages give you the strongest career momentum right now.",
    tag: "Coding",
  },
  {
    id: 3,
    name: "How smart devices are changing the way we live",
    avatar: "/blog/latest-2.png",
    description:
      "Connected homes, wearables and voice assistants are automating our daily routines. Here is what the smart revolution looks like today.",
    tag: "IoT",
  },
  {
    id: 4,
    name: "Why open source is winning the future of software",
    avatar: "/blog/latest-news.png",
    description:
      "From Linux to Kubernetes, open source powers the modern internet. Discover the community model driving the next decade of innovation.",
    tag: "Open Source",
  },
  {
    id: 5,
    name: "A beginner's guide to setting up home automation",
    avatar: "/blog/home-automation.svg",
    description:
      "Smart bulbs to security cameras — get a practical walkthrough for building a connected home that simplifies your everyday life.",
    tag: "Smart Home",
  },
  {
    id: 6,
    name: "Staying safe on public free Wi-Fi",
    avatar: "/blog/wifi-security.svg",
    description:
      "Free hotspots are convenient but risky. Learn the simple habits and tools that keep your data private wherever you connect.",
    tag: "Security",
  },
];

export default function Technology() {
  const [tests, setTests] = useState<TechPost[]>(dummyTests);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("https://localhost:7246/api/Tests");
        const data = await response.json();
        if (data && data.result?.length) {
          setTests(data.result);
        }
      } catch (error) {
        // Keep the dummy data when the backend is unreachable.
        console.warn("Technology API unavailable, showing sample posts:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="py-10">
      <div className="bg-white py-5 md:px-12 px-4 flex justify-between items-center rounded-lg shadow-sm">
        <h2 className="font-semibold capitalize text-xl md:text-3xl relative">
          <span>Technology</span>
          <div className="absolute -bottom-4 left-0 h-[6px] w-full bg-secondary"></div>
        </h2>
        <p className="text-primary text-xs md:text-base hover:underline cursor-pointer">
          View more Articles
        </p>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-7 mt-8">
        {tests.map((test) => (
          <Link
            to="/blog-details"
            key={test.id}
            className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 border border-gray-100 flex flex-col"
          >
            <div className="relative h-44 md:h-48 overflow-hidden">
              <img
                src={test.avatar.startsWith("/") || test.avatar.startsWith("http")
                  ? test.avatar
                  : `https://localhost:7246/${test.avatar}`}
                alt={test.name}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent group-hover:from-black/55 transition-colors duration-300"></div>
              <span className="absolute top-4 left-4 px-3 py-1 text-xs font-semibold rounded-full bg-white/95 text-primary shadow-sm group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                {test.tag}
              </span>
            </div>
            <div className="p-6 flex flex-col gap-3 flex-1">
              <h2 className="text-lg md:text-xl font-semibold leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                {test.name}
              </h2>
              <p className="text-sm text-gray-600 line-clamp-2">{test.description}</p>
              <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                <span className="flex items-center gap-1.5">
                  <AiFillClockCircle /> 1 week ago
                </span>
                <span className="flex items-center gap-1.5">
                  <BiSolidMessageAlt /> 67
                </span>
              </div>
              <span className="mt-auto pt-1 inline-flex items-center gap-2 text-sm font-semibold text-primary group-hover:gap-3 transition-all duration-300">
                Continue Reading
                <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                  <AiOutlineArrowRight className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </span>
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
